import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import type { BLSState, BLSAction } from '../types';

/**
 * BLSContext - Global state management for BLS settings
 *
 * Manages:
 * - Current BLS configuration (color, speed, sound, etc.)
 * - Play/stop state
 * - State synchronization between therapist and client
 */

interface BLSContextValue {
  state: BLSState;
  dispatch: React.Dispatch<BLSAction>;
}

const BLSContext = createContext<BLSContextValue | undefined>(undefined);

// Initial state
const initialState: BLSState = {
  isPlaying: false,
  color: '#1471ed', // Default blue
  speed: 3, // Display level 3 (maps to 0.8 Hz internally)
  volume: 0.7, // 70%
  selectedSounds: ['off'], // Default to no sound
  direction: 'horizontal',
  circleSize: 96, // 96px (w-24 in Tailwind)
  muteTherapistAudio: false, // Default: therapist hears audio
  background: '#DBEAFE', // Default: Soft Blue (calming)
  movementStyle: 'smooth',
  mixedMode: false, // Mixed mode off by default
  mixedModeConfig: {
    randomizeColor: false,
    randomizeDirection: false,
    randomizeMovementStyle: false,
    randomizeBackground: false,
    randomizeShape: false,
    randomizeSounds: false,
  },
  shape: 'ball', // 3D ball is the default
  visualEnabled: true, // Visual enabled by default
  audioEnabled: true, // Audio enabled by default
  audioMultiSelect: false, // Single-select mode by default
};

// Reducer
function blsReducer(state: BLSState, action: BLSAction): BLSState {
  switch (action.type) {
    case 'START':
      return {
        ...state,
        isPlaying: true,
      };

    case 'STOP':
      return {
        ...state,
        isPlaying: false,
      };

    case 'SET_COLOR':
      return {
        ...state,
        color: action.color,
      };

    case 'SET_SPEED':
      return {
        ...state,
        speed: action.speed,
      };

    case 'SET_VOLUME':
      return {
        ...state,
        volume: action.volume,
      };

    case 'SET_SELECTED_SOUNDS':
      return {
        ...state,
        selectedSounds: action.sounds,
      };

    case 'SET_DIRECTION':
      return {
        ...state,
        direction: action.direction,
      };

    case 'SET_CIRCLE_SIZE':
      return {
        ...state,
        circleSize: action.size,
      };

    case 'SET_MUTE_THERAPIST_AUDIO':
      return {
        ...state,
        muteTherapistAudio: action.muted,
      };

    case 'SET_BACKGROUND':
      return {
        ...state,
        background: action.background,
      };

    case 'SET_MOVEMENT_STYLE':
      return {
        ...state,
        movementStyle: action.style,
      };

    case 'SET_MIXED_MODE':
      return {
        ...state,
        mixedMode: action.enabled,
      };

    case 'SET_MIXED_MODE_CONFIG':
      return {
        ...state,
        mixedModeConfig: { ...state.mixedModeConfig, ...action.config },
      };

    case 'TOGGLE_ALL_RANDOM':
      // When All Random is toggled ON, turn on all individual configs
      // When toggled OFF, turn off all individual configs
      return {
        ...state,
        mixedMode: action.enabled,
        mixedModeConfig: {
          randomizeColor: action.enabled,
          randomizeDirection: action.enabled,
          randomizeMovementStyle: action.enabled,
          randomizeBackground: action.enabled,
          randomizeShape: action.enabled,
          randomizeSounds: action.enabled,
        },
      };

    case 'SET_SHAPE':
      return {
        ...state,
        shape: action.shape,
      };

    case 'SET_VISUAL_ENABLED':
      return {
        ...state,
        visualEnabled: action.enabled,
      };

    case 'SET_AUDIO_ENABLED':
      return {
        ...state,
        audioEnabled: action.enabled,
      };

    case 'SET_AUDIO_MULTI_SELECT':
      return {
        ...state,
        audioMultiSelect: action.enabled,
      };

    case 'SET_STATE':
      return {
        ...state,
        ...action.state,
      };

    default:
      return state;
  }
}

// Provider component
interface BLSProviderProps {
  children: ReactNode;
}

export function BLSProvider({ children }: BLSProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(blsReducer, initialState);

  return (
    <BLSContext.Provider value={{ state, dispatch }}>
      {children}
    </BLSContext.Provider>
  );
}

// Custom hook to use BLS context
export function useBLSContext(): BLSContextValue {
  const context = useContext(BLSContext);
  if (context === undefined) {
    throw new Error('useBLSContext must be used within a BLSProvider');
  }
  return context;
}
